import { useSearchParamsContext } from '@internal/blocks/SearchResultsBlock/SearchParamsContext';
import { TSearchParams } from '@internal/blocks/SearchResultsBlock/TSearchParams';
import { KnownBaleChamberType } from '@internal/utils/machine/KnownBalerChamberType';
import { KnownMachineHeaderType } from '@internal/utils/machine/KnownMachineHeaderType';
import { MachineType } from '@internal/utils/machine/MachineType';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { RadioSelectFilter } from '@renderer-ui-library/molecules';
import { SearchFilterCategory } from '@renderer-ui-library/organisms';
import { useSearchResultsRouting } from 'blocks/SearchResultsBlock/useSearchResultsRouting';
import { translations } from 'i18n/translations';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import { tracker } from 'utils/tracking/tracker';

export const RadioFilter: React.FC = React.memo(() => {
    const { searchParams } = useSearchParamsContext();
    const { machineType, baleChamber, headerType } = searchParams;
    const { gotoSearchResults } = useSearchResultsRouting();
    const { t } = useTranslation(localeNamespace.common);

    const value = useMemo(() => {
        if (machineType === MachineType.BALER) {
            return baleChamber;
        }
        if (machineType === MachineType.HEADER) {
            return headerType;
        }
        return undefined;
    }, [baleChamber, headerType, machineType]);

    const title = useMemo(() => {
        if (machineType === MachineType.BALER) {
            return t(translations.searchResultsFiltersBaleChamber);
        }
        if (machineType === MachineType.HEADER) {
            return t(translations.headerTypeFilterTitle);
        }
        return '';
    }, [machineType, t]);

    const handleChange = useCallback(
        (value: KnownBaleChamberType | KnownMachineHeaderType | null) => {
            let key:
                | keyof Pick<TSearchParams, 'baleChamber' | 'headerType'>
                | undefined;

            switch (machineType) {
                case MachineType.BALER:
                    key = 'baleChamber';
                    break;
                case MachineType.HEADER:
                    key = 'headerType';
                    break;
                default:
                    return;
            }

            if (value) {
                tracker.trackEvent({
                    name: 'filter-applied',
                    filter_placement: 'serp',
                    filter_category: 'filter-other',
                    filter_attribute: key,
                    filter_value: value,
                });
            }

            gotoSearchResults({
                ...searchParams,
                [key]: value ?? undefined,
                amount: undefined,
            });
        },
        [gotoSearchResults, machineType, searchParams],
    );

    return (
        <SearchFilterCategory title={title}>
            <RadioSelectFilter
                value={value}
                onChange={handleChange}
                canToggle
            />
        </SearchFilterCategory>
    );
});

RadioFilter.displayName = 'RadioFilter';
